import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaCm030 } from '~/endpoints/ApiTaCm030'
import { ApiTaRe190 } from '~/endpoints/ApiTaRe190'
import { ApiTaRe200 } from '~/endpoints/ApiTaRe200'
import { ApiTaRe210 } from '~/endpoints/ApiTaRe210'
import { ApiTaRe220 } from '~/endpoints/ApiTaRe220'
import compareSubjectOrder from '~/utils/subjects'

// State types
// ApiTaCm030
interface MonthList {
  reportType: number
  reportList: {
    reportId: string
    memNum: string
    grade: number
    fiscalYear: number
    attendedMonth: number
    testCountPerMonth: number
    isRead?: boolean
    testType: number
  }[]
}
const getEmptyMonthList = (): MonthList => ({
  reportType: 0,
  reportList: [],
})

// ApiTaRe190
interface LearningAssessmentScore {
  fixabilityTestResults: {
    memNum: string
    title: string
    gradeDataResults: {
      subjectCode: string
      answerId: string
      score: string
      average: string
      deviationValue: string
    }[]
    difficultyResults: {
      subjectCode: string
      resultsByQuestion: {
        questionNo: number
        basicsScoreRate: string
        practicalScoreRate: string
      }[]
    }[]
  }
}
const getEmptyLearningAssessmentScore = (): LearningAssessmentScore => ({
  fixabilityTestResults: {
    memNum: '',
    title: '',
    gradeDataResults: [],
    difficultyResults: [],
  },
})

// ApiTaRe200
interface TestRetentionSubjects {
  results: {
    largePartNo: string
    smallPartNo: string
    difficultyLevel: string
    allocation: string
    score: string
    averageScore: string
  }[]
}
const getEmptyTestRetentionSubjects = (): TestRetentionSubjects => ({
  results: [],
})

// ApiTaRe210
interface AbilityTestRankTryFixation {
  results: {
    ranking: string
    score: string
    nickName: string
    schoolName: string
  }[]
}
const getEmptyAbilityTestRankTryFixation = (): AbilityTestRankTryFixation => ({
  results: [],
})

// ApiTaRe220
interface AbilityTestTryFixation {
  abilityTestReport: {
    scoreList: {
      subjectCode: string
      score: string
    }[]
    choiceSchoolScoreList: {
      choiceSchoolName: string
      averageList:
        | {
            subjectCode: string
            averageScore: string
          }[]
        | null
    }[]
  }
}
const getEmptyAbilityTestTryFixation = (): AbilityTestTryFixation => ({
  abilityTestReport: {
    scoreList: [],
    choiceSchoolScoreList: [],
  },
})

// State action parameter
// ApiTaCm030
interface ActionParamsMonthList {
  reportType: number
  offset: string
  limit: number
}

// ApiTaRe190
interface ActionParamsLearningAssessmentScore {
  fiscalYear: number
  month: number
  times?: number
  grade: number
  courseId: string
}

// ApiTaRe200
interface ActionParamsTestRetentionSubjects {
  subjectCode: string
  grade: number
  fiscalYear: number
  testMonth: number
  testCountPerMonth?: number
  courseId: string
}

// ApiTaRe210
interface ActionParamsAbilityTestRankTryFixation {
  fiscalYear: number
  testMonth: number
  testCountPerMonth?: number
  subjectCode: string
  grade: number
  courseId: string
}

// ApiTaRe220
interface ActionParamsAbilityTestTryFixation {
  fiscalYear: number
  testMonth: number
  testCountPerMonth?: number
  grade: number
  courseId: string
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/FixationLevel',
})
export default class FixationLevel extends VuexModule {
  // States
  // ApiTaCm030
  private monthList_ = getEmptyMonthList()
  private hasMoreMonthList_ = true

  // ApiTaRe190
  private learningAssessmentScore_ = getEmptyLearningAssessmentScore()

  // ApiTaRe200
  private testRetentionSubjects_ = getEmptyTestRetentionSubjects()

  // ApiTaRe210
  private abilityTestRankTryFixation_ = getEmptyAbilityTestRankTryFixation()

  // ApiTaRe220
  private abilityTestTryFixation_ = getEmptyAbilityTestTryFixation()

  // Getters
  // ApiTaCm030
  get monthList(): MonthList {
    return this.monthList_
  }

  get hasMoreMonthList(): boolean {
    return this.hasMoreMonthList_
  }

  // ApiTaRe190
  get learningAssessmentScore(): LearningAssessmentScore {
    return this.learningAssessmentScore_
  }

  // ApiTaRe200
  get testRetentionSubjects(): TestRetentionSubjects {
    return this.testRetentionSubjects_
  }

  // ApiTaRe210
  get abilityTestRankTryFixation(): AbilityTestRankTryFixation {
    return this.abilityTestRankTryFixation_
  }

  // ApiTaRe220
  get abilityTestTryFixation(): AbilityTestTryFixation {
    return this.abilityTestTryFixation_
  }

  // Mutations
  // ApiTaCm030
  @Mutation
  updateMonthList(value: MonthList) {
    this.monthList_.reportType = value.reportType
    this.monthList_.reportList = this.monthList_.reportList.concat(value.reportList)
  }

  @Mutation
  updateMonthListReadFlg(index: number) {
    this.monthList_.reportList[index].isRead = true
  }

  @Mutation
  clearMonthList() {
    this.monthList_ = getEmptyMonthList()
  }

  @Mutation
  updateHasMoreMonthList(flg: boolean) {
    this.hasMoreMonthList_ = flg
  }

  @Mutation
  clearHasMoreMonthList() {
    this.hasMoreMonthList_ = true
  }

  // ApiTaRe190
  @Mutation
  updateLearningAssessmentScore(value: LearningAssessmentScore) {
    this.learningAssessmentScore_ = value
  }

  // ApiTaRe200
  @Mutation
  updateTestRetentionSubjects(value: TestRetentionSubjects) {
    this.testRetentionSubjects_ = value
  }

  // ApiTaRe210
  @Mutation
  updateAbilityTestRankTryFixation(value: AbilityTestRankTryFixation) {
    this.abilityTestRankTryFixation_ = value
  }

  // ApiTaRe220
  @Mutation
  updateAbilityTestTryFixation(value: AbilityTestTryFixation) {
    this.abilityTestTryFixation_ = value
  }

  // Actions

  @Action
  clear() {
    this.context.commit('clearMonthList')
    this.context.commit('clearHasMoreMonthList')
    this.context.commit('updateLearningAssessmentScore', getEmptyLearningAssessmentScore())
    this.context.commit('updateTestRetentionSubjects', getEmptyTestRetentionSubjects())
    this.context.commit('updateAbilityTestRankTryFixation', getEmptyAbilityTestRankTryFixation())
    this.context.commit('updateAbilityTestTryFixation', getEmptyAbilityTestTryFixation())
  }

  @Action
  async refreshMonthList(param: ActionParamsMonthList) {
    logger.trace('in: refreshMonthList')
    const api = new ApiTaCm030.Api()

    this.context.commit('updateHasMoreMonthList', false)

    // API: ApiTaCm030
    const result = await api.get<MonthList | null>(
      {
        query: {
          reportType: param.reportType,
          offset: param.offset,
          limit: param.limit,
          courseType: 'A',
        },
      },
      // success
      (data, _) => {
        return {
          reportType: data.reportType,
          reportList: data.reportList,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null || result === undefined) {
      this.context.commit('updateHasMoreMonthList', true)
      return
    }
    this.context.commit('updateHasMoreMonthList', result.reportList.length === param.limit)
    this.context.commit('updateMonthList', result)
  }

  @Action
  setMonthListReadFlg(index: number) {
    this.context.commit('updateMonthListReadFlg', index)
  }

  // ApiTaRe190
  @Action
  async refreshLearningAssessmentScore(param: ActionParamsLearningAssessmentScore) {
    logger.trace('in: refreshLearningAssessmentScore')
    const api = new ApiTaRe190.Api()
    const request: ApiTaRe190.Get.Request = {
      query: {
        fiscalYear: param.fiscalYear,
        month: param.month,
        grade: param.grade,
        courseId: param.courseId,
      },
    }
    if (param.times) {
      request.query.times = param.times
    }

    // API: ApiTaRe190
    const result = await api.get<LearningAssessmentScore | null>(
      request,

      // success
      (data, _) => {
        return {
          fixabilityTestResults: {
            memNum: data.fixabilityTestResults.memNum,
            title: data.fixabilityTestResults.title,
            gradeDataResults: data.fixabilityTestResults.gradeDataResults
              .map(elem => {
                return {
                  subjectCode: elem.subjectCode,
                  answerId: elem.answerId,
                  score: elem.score,
                  average: elem.average,
                  deviationValue: elem.deviationValue,
                }
              })
              .sort((a, b) => {
                // NOTE: Fortuna V2に移管するにあたり、明示的にソートする
                return compareSubjectOrder(a.subjectCode, b.subjectCode)
              })
              .filter(item => {
                // 総合成績の時点では社会3教科は利用しないため非表示
                if (item.subjectCode === 'S3J') {
                  return false
                }
                return true
              }),
            difficultyResults: data.fixabilityTestResults.difficultyResults,
          },
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateLearningAssessmentScore', result)
  }

  // ApiTaRe200
  @Action
  async refreshTestRetentionSubjects(param: ActionParamsTestRetentionSubjects) {
    logger.trace('in: refreshTestRetentionSubjects')
    const api = new ApiTaRe200.Api()
    const request: ApiTaRe200.Get.Request = {
      query: {
        subjectCode: param.subjectCode,
        grade: param.grade,
        fiscalYear: param.fiscalYear,
        testMonth: param.testMonth,
        courseId: param.courseId,
      },
    }
    if (param.testCountPerMonth) {
      request.query.testCountPerMonth = param.testCountPerMonth
    }

    // API: ApiTaRe200
    const result = await api.get<TestRetentionSubjects | null>(
      request,

      // success
      (data, _) => {
        return {
          results: data.results,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateTestRetentionSubjects', result)
  }

  // ApiTaRe210
  @Action
  async refreshAbilityTestRankTryFixation(param: ActionParamsAbilityTestRankTryFixation) {
    logger.trace('in: refreshAbilityTestRankTryFixation')
    const api = new ApiTaRe210.Api()
    const request: ApiTaRe210.Get.Request = {
      query: {
        fiscalYear: param.fiscalYear,
        testMonth: param.testMonth,
        subjectCode: param.subjectCode,
        grade: param.grade,
        courseId: param.courseId,
      },
    }
    if (param.testCountPerMonth) {
      request.query.testCountPerMonth = param.testCountPerMonth
    }

    // API: ApiTaRe210
    const result = await api.get<AbilityTestRankTryFixation | null>(
      request,

      // success
      (data, _) => {
        return {
          results: data.results,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateAbilityTestRankTryFixation', result)
  }

  // ApiTaRe220
  @Action
  async refreshAbilityTestTryFixation(param: ActionParamsAbilityTestTryFixation) {
    logger.trace('in: refreshAbilityTestTryFixation')
    const api = new ApiTaRe220.Api()
    const request: ApiTaRe220.Get.Request = {
      query: {
        fiscalYear: param.fiscalYear,
        testMonth: param.testMonth,
        grade: param.grade,
        courseId: param.courseId,
      },
    }
    if (param.testCountPerMonth) {
      request.query.testCountPerMonth = param.testCountPerMonth
    }

    // API: ApiTaRe220
    const result = await api.get<AbilityTestTryFixation | null>(
      request,

      // success
      (data, _) => {
        return {
          abilityTestReport: {
            ...data.abilityTestReport,
            scoreList: data.abilityTestReport.scoreList
              .sort((a, b) => {
                return compareSubjectOrder(a.subjectCode, b.subjectCode)
              })
              .filter(item => item.subjectCode !== '3J'),
            choiceSchoolScoreList: data.abilityTestReport.choiceSchoolScoreList.map(choiceSchoolScore => {
              return {
                choiceSchoolName: choiceSchoolScore.choiceSchoolName,
                averageList:
                  choiceSchoolScore.averageList
                    ?.sort((a, b) => {
                      return compareSubjectOrder(a.subjectCode, b.subjectCode)
                    })
                    .filter(item => item.subjectCode !== '3J') ?? null,
              }
            }),
          },
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateAbilityTestTryFixation', result)
  }
}
